import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Paper,
  Theme,
  Typography
} from '@material-ui/core';
import DoneIcon from '@material-ui/icons/Done';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import PauseIcon from '@material-ui/icons/Pause';
import React from 'react';
import { useIntl } from 'react-intl';
import RouteLeavingGuard from '../../components/routeLeaving/routeLeaving';
import useWindowUnload from '../components/form/hooks/usePreventWindowUnload';
import { messages } from './BulkImportCheckpoints.translations';
import { ImportProgress, ImportProgressStatus } from './BulkImportReducer';

interface ListItemProps {
  progressReport: ImportProgress;
}

function ProgressListIcon({ status }: { status: ImportProgressStatus }) {
  switch (status) {
    case 'WAIT':
      return <PauseIcon />;
    case 'READY':
      return <CircularProgress color="inherit" />;
    case 'SUCCESS':
      return <DoneIcon />;
    case 'FAIL':
      return <ErrorOutlineIcon />;
    default:
      throw Error(`Unexpected input for icon selection ${status}`);
  }
}

interface ProgressBarProps {
  done: number;
  total: number;
}

function LinearProgressWithLabel({ done, total }: ProgressBarProps) {
  return (
    <Box display="flex" alignItems="center">
      <Box width="100%" mr={1}>
        <LinearProgress variant="determinate" value={done / total * 100} />
      </Box>
      <Box minWidth={55}>
        <Typography variant="body2" color="textSecondary">{`${done}/${total}`}</Typography>
      </Box>
    </Box>
  );
}

function ProgressListItem({ progressReport }: ListItemProps) {
  return (
    <ListItem divider={true}>
      <ListItemText
        primary={progressReport.name}
        secondary={progressReport.message}
        secondaryTypographyProps={{ color: 'error' }}
      />
      <ListItemSecondaryAction>
        <ProgressListIcon status={progressReport.status} />
      </ListItemSecondaryAction>
    </ListItem>
  );

}

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    marginTop: theme.spacing(1)
  },
  paper: {
    maxHeight: 400,
    overflow: 'auto'
  },
  typography: {
    marginLeft: theme.spacing(1)
  }
}));

export interface ImportProgressProps {
  progressReports: ImportProgress[];
  handleCancel: () => void;
  handleConfirm: () => void;
}

const BulkImportProgress = ({ progressReports, handleCancel, handleConfirm }: ImportProgressProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const complete = progressReports.every(report => report.status === 'SUCCESS' || report.status === 'FAIL');

  useWindowUnload(!complete);

  return (
    <>
      <Paper className={classes.paper}>
        <Typography className={classes.typography} variant="h6">{formatMessage(messages['checkpoint.bulkImport.progress.title'])}</Typography>
        <Typography className={classes.typography} variant="subtitle2" >
          {formatMessage(messages['checkpoint.bulkImport.progress.subtitle'])}
        </Typography>
        <LinearProgressWithLabel
          total={progressReports.length}
          done={progressReports.filter(x => x.status === 'SUCCESS' || x.status === 'FAIL').length}
        />
        <List
          dense={true}
        >
          {progressReports
            .map(progressReport => <ProgressListItem key={progressReport.name} progressReport={progressReport} />)}
        </List>
      </Paper>
      <Grid container={true} alignItems="flex-start" justify="flex-end" direction="row">
        <ButtonGroup variant="contained" size="large" className={classes.button}>
          <Button
            disabled={complete}
            color="secondary"
            onClick={handleCancel}
          >
            {formatMessage(messages['checkpoint.bulkImport.formHeader.cancel'])}
          </Button>
          <Button
            disabled={!complete}
            color="primary"
            onClick={handleConfirm}
          >
            {formatMessage(messages['checkpoint.bulkImport.formHeader.confirm'])}
          </Button>
        </ButtonGroup>
      </Grid>
      <RouteLeavingGuard when={!complete}/>
    </>
  );
};

export default BulkImportProgress;
